/**
 * What this site looked like the last twelve times we measured it.
 *
 * The one sentence this panel exists to produce sits at the very top: "since the
 * 12th they have fixed two of these". It is the strongest opening a second call
 * in this funnel has, and it is worth nothing three screens down, so the panel
 * leads with it and the run list below is the evidence for it.
 *
 * THREE THINGS HERE ARE DELIBERATE AND EASY TO UNDO BY ACCIDENT:
 *
 * 1. LOADING AND "NEVER MEASURED BEFORE" ARE DIFFERENT SENTENCES. An empty
 *    history is a claim about the site, and an operator who reads it during the
 *    first two hundred milliseconds will go and start a run they did not need.
 *    The peer comparison next door is allowed to blur the two because absence is
 *    its ordinary answer; this panel is not.
 * 2. THE WITHHELD LINE IS NOT NOISE. The server refuses to call a defect fixed
 *    when the newer run did not measure that family completely, and says which
 *    family it left out. Dropping that line would turn a partial comparison into
 *    what reads like a complete one, which is the whole failure this feature was
 *    built to avoid. Measured in production on 2026-08-19, this line is the
 *    common case rather than the exception: of six stored runs only one has a
 *    usable layout.
 *
 *    ⚠ LINKS ARE THE ONE FAMILY WHERE "WITHHELD" IS NOT THE WHOLE TRUTH. Every
 *    run hits the link-check budget (60 of up to 281), so the family is
 *    permanently incomplete - but dead links are compared one address at a time,
 *    over the addresses both runs got an answer about. So when `linksCompared`
 *    is non-zero the panel says how big that ground is INSTEAD of listing links
 *    as left out: "only these 47 addresses were compared" is true, and "the
 *    links were left out" would be a lie about a comparison that just proved a
 *    repaired link.
 * 3. A RUN THAT MEASURED NOTHING SAYS SO. It gets no comparison at all, because
 *    an empty diff and a site with nothing wrong on it look identical.
 *
 * 4. THIS PANEL COUNTS A DIFFERENT NUMBER FROM THE FINDINGS TAB, AND NOW SAYS SO.
 *    The history table stores the crawl, the links and the probe, and nothing the
 *    model said. So a run that the Findings tab shows with nine defects appears
 *    here with the four that were measured, and until this was worded the same
 *    card carried two counts for one run with nothing to explain the gap. Every
 *    count in this panel is therefore named as MEASURED, and the footer says once
 *    that the model's findings are outside the comparison entirely.
 *
 *    ⚠ THAT IS NOT THE SAME AS "WITHHELD". The withheld line names families two
 *    runs could not both stand behind THIS TIME. The model's findings are not in
 *    the comparison at all, by design, on every run: an unchanged site does not
 *    guarantee an unchanged answer, and an unclear vision answer emits no finding
 *    at all, so their silence can never be read as a repair. Putting them in the
 *    withheld line would promise they arrive once some run measures better.
 */
import { ArrowLeftRight, EyeOff, ExternalLink, Loader2, MinusCircle, PlusCircle } from "lucide-react";
import { starterLabel } from "../../../../lib/brainStarter";
import type { BrainFinding, BrainHistoryRun, BrainRunDelta } from "../../../../lib/siteBrain";
import { StatusPill } from "../../../ui";
import type { T } from "../../site-scanner/types";
import { findingText } from "./findingText";
import type { UseBrainHistory } from "./useHistory";
import { VerdictDriftMark } from "./VerdictDriftMark";

/** The verdict colours, the same three `BrainFit` uses above the tabs. */
const FIT_TONE = {
  go: "ok",
  hold: "wt",
  no: "lt",
} as const;

export function BrainHistory({
  state,
  me,
  t,
}: {
  state: UseBrainHistory;
  /** The reader's own user id, so their own run says "you". */
  me: string;
  t: T;
}) {
  if (state.loading) {
    return (
      <div className="flex justify-center py-6 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (state.failed) {
    return <p className="text-sm text-muted-foreground">{t("brain.history.failed")}</p>;
  }

  const runs = state.history?.runs ?? [];
  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("brain.history.empty")}</p>;
  }

  const newest = runs[0];
  const fixed = newest?.delta?.fixed.length ?? 0;

  return (
    <div className="space-y-3">
      {/* The call opener. Only when there is something to open with: a headline
          that has to say "nothing has changed" is a headline nobody reads twice. */}
      {newest?.delta && fixed > 0 ? (
        <p
          className="rounded-2xl bg-[var(--crm-sunk)] px-4 py-3 text-sm font-semibold"
          style={{ boxShadow: "inset 3px 0 0 0 color-mix(in oklab, var(--color-success) 70%, transparent)" }}
        >
          {t("brain.history.headline", { n: fixed, date: day(newest.delta.previousAt) })}
        </p>
      ) : null}

      <ul className="space-y-2">
        {runs.map((run) => (
          <RunRow key={run.runSeq} run={run} me={me} t={t} />
        ))}
      </ul>

      {/* Said out loud once the list is full, so a missing older run reads as a
          policy rather than as data somebody lost. */}
      {state.history && runs.length >= state.history.keep ? (
        <p className="text-xs text-muted-foreground">{t("brain.history.capped", { n: state.history.keep })}</p>
      ) : null}

      {/* ONCE, AT THE FOOT, AND NOT PER RUN. It is true of every comparison in
          the list, so twelve copies would be twelve lines of the same sentence
          pushing the evidence off the screen. Down here it sits with the other
          note about how this list works, which is where a reader who has just
          noticed the count does not match the Findings tab will look. */}
      <p className="text-xs text-muted-foreground">{t("brain.history.aiNotCompared")}</p>
    </div>
  );
}

function RunRow({ run, me, t }: { run: BrainHistoryRun; me: string; t: T }) {
  const starter = starterLabel(run, me);
  const measured = run.measured.length > 0 || run.findings.length > 0;

  return (
    <li className="space-y-1.5 rounded-xl bg-[var(--crm-sunk)] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="crm-tnum font-semibold">{day(run.finishedAt)}</span>

        {run.fit ? (
          <StatusPill tone={FIT_TONE[run.fit]}>{t(`brain.fit.decision.${run.fit}`)}</StatusPill>
        ) : null}

        {/* Beside the pill rather than down in the delta, so the caution and the
            thing it cautions against are read in one glance. The delta below
            spells the same fact out in words for the reader who is already
            there. */}
        <VerdictDriftMark drift={run.delta?.verdictDrift} t={t} />

        {/* ⚠ NOT SHOWN FOR A RUN THAT MEASURED NOTHING. "0 defects" beside "this
            run measured nothing" is the same lie twice: a zero reads as a clean
            site, which is the one thing a failed run cannot tell us.

            The word "measured" is load-bearing, not padding: the Findings tab of
            this same card counts the model's findings too, so the bare number
            here is smaller than the one two tabs away for the same run. */}
        {measured ? (
          <span className="text-muted-foreground">{t("brain.history.findings", { n: run.findings.length })}</span>
        ) : null}

        {starter ? (
          <span className="text-xs text-muted-foreground">
            {"name" in starter ? t("brain.startedBy.name", { name: starter.name }) : t(`brain.startedBy.${starter.key}`)}
          </span>
        ) : null}
      </div>

      {/* The run's own trouble, in the same words the card uses above the tabs. */}
      {run.status === "error" ? (
        <p className="text-xs text-muted-foreground">{t(`brain.runError.${run.error ?? "failed"}`)}</p>
      ) : null}

      {run.degraded.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t("brain.degraded", {
            stages: run.degraded.map((stage) => t(`brain.degradedStage.${stage}`)).join(", "),
          })}
        </p>
      ) : null}

      {run.delta ? <Delta delta={run.delta} t={t} /> : <Uncompared run={run} t={t} />}
    </li>
  );
}

/**
 * Why this run is not compared with anything.
 *
 * Two different reasons and they need different words: the oldest run kept has
 * nothing behind it, while a run that measured nothing cannot be compared with
 * anything at all. Collapsing them would tell an operator their oldest baseline
 * is a broken run.
 */
function Uncompared({ run, t }: { run: BrainHistoryRun; t: T }) {
  const measured = run.measured.length > 0 || run.findings.length > 0;
  if (!measured) return <p className="text-xs text-muted-foreground">{t("brain.history.nothingMeasured")}</p>;
  return <p className="text-xs text-muted-foreground">{t("brain.history.baseline")}</p>;
}

function Delta({ delta, t }: { delta: BrainRunDelta; t: T }) {
  const quiet = delta.fixed.length === 0 && delta.appeared.length === 0;

  // Links compared address by address: the family is incomplete, but a repair
  // inside the shared addresses is still provable. Saying both "left out" and
  // "47 compared" would contradict itself, so the count takes links out of the
  // withheld line and speaks for it.
  const linksPartly = delta.withheld.includes("links") && delta.linksCompared > 0;
  const families = linksPartly ? delta.withheld.filter((family) => family !== "links") : delta.withheld;

  return (
    <div className="space-y-1">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>{t("brain.history.since", { date: day(delta.previousAt) })}</span>
        {quiet ? (
          <span>{t("brain.history.noChange")}</span>
        ) : (
          <>
            {delta.fixed.length > 0 ? (
              <span style={{ color: "var(--color-success)" }}>
                {t("brain.history.fixed", { n: delta.fixed.length })}
              </span>
            ) : null}
            {delta.appeared.length > 0 ? (
              <span style={{ color: "var(--color-warning)" }}>
                {t("brain.history.appeared", { n: delta.appeared.length })}
              </span>
            ) : null}
            {delta.stayed > 0 ? <span>{t("brain.history.stayed", { n: delta.stayed })}</span> : null}
          </>
        )}
      </p>

      {delta.fixed.length > 0 ? (
        <ChangeList findings={delta.fixed} icon={MinusCircle} colour="var(--color-success)" t={t} />
      ) : null}
      {delta.appeared.length > 0 ? (
        <ChangeList findings={delta.appeared} icon={PlusCircle} colour="var(--color-warning)" t={t} />
      ) : null}

      {/* The hole in the comparison, named. See the file header. */}
      {families.length > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {/* An eye that was closed, not a spanner. The families in this line
              were not looked at; nothing about them is being repaired. */}
          <EyeOff className="size-3 shrink-0" />
          {t("brain.history.withheld", {
            families: families.map((family) => t(`brain.history.family.${family}`)).join(", "),
          })}
        </p>
      ) : null}

      {/* The ground a dead-link claim stands on, when there is one. Same eye:
          this is still a statement about what was NOT looked at, said from the
          other end, and it replaces links in the line above rather than adding
          to it. */}
      {linksPartly ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <EyeOff className="size-3 shrink-0" />
          {t("brain.history.linksCompared", { n: delta.linksCompared })}
        </p>
      ) : null}

      {/* Spelled out, because this panel is where somebody comes to find out
          WHY. The mark beside the pill above is the glance version; this is the
          sentence, and it sits under the line that just said nothing changed,
          which is exactly the contradiction it explains. */}
      {delta.verdictDrift ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ArrowLeftRight className="mt-0.5 size-3 shrink-0" />
          <span>
            {t("brain.verdictDrift.line", {
              from: t(`brain.fit.decision.${delta.verdictDrift.from}`),
              to: t(`brain.fit.decision.${delta.verdictDrift.to}`),
            })}
          </span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The defects that went, or arrived.
 *
 * Rendered through the same `findingText` the findings tab uses, so a sentence an
 * operator reads out loud here is word for word the one they read there. Two
 * renderers drifting apart would mean the proof under a repair disagreeing with
 * the proof in the defect list.
 */
function ChangeList({
  findings,
  icon: Icon,
  colour,
  t,
}: {
  findings: BrainFinding[];
  icon: typeof MinusCircle;
  colour: string;
  t: T;
}) {
  return (
    <ul className="space-y-0.5">
      {findings.map((finding) => (
        <li key={finding.id} className="flex items-start gap-1.5 text-xs">
          <Icon className="mt-0.5 size-3 shrink-0" style={{ color: colour }} />
          <div className="min-w-0 flex-1">
            <p className="leading-snug">{findingText(finding, t)}</p>
            {/* ⚠ THE ADDRESS IS NOT DECORATION. Two dead links render the same
                sentence, so without it "they fixed a dead link" cannot be said
                about a particular one - and naming the address is most of the
                persuasion. Rendered exactly as the findings tab renders it, a
                link the operator can open to see the repair for themselves. */}
            {finding.evidence.url ? (
              <a
                href={finding.evidence.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex max-w-full items-center gap-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                <ExternalLink className="size-3 shrink-0" />
                <span className="truncate">{finding.evidence.url}</span>
              </a>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** The date, as the rest of this card writes it. */
const day = (iso: string): string => iso.slice(0, 10);
